import React, { useEffect, useRef, useState } from 'react';
import { MapPin, Search } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const GOOGLE_MAPS_SCRIPT_ID = 'magnolia-google-maps';
let googleMapsPromise = null;

const NIGERIA_LATITUDE_BOUNDS = [4, 14.5];
const NIGERIA_LONGITUDE_BOUNDS = [2, 15.5];
const INVALID_KEY_VALUES = new Set(['', 'none', 'null', 'undefined', 'false', 'your-key-here']);

const sanitizeApiKey = (value) => {
  const key = String(value || '').trim();
  return INVALID_KEY_VALUES.has(key.toLowerCase()) ? '' : key;
};

const isNigeriaCoordinate = (lat, lng) => (
  lat >= NIGERIA_LATITUDE_BOUNDS[0] &&
  lat <= NIGERIA_LATITUDE_BOUNDS[1] &&
  lng >= NIGERIA_LONGITUDE_BOUNDS[0] &&
  lng <= NIGERIA_LONGITUDE_BOUNDS[1]
);

const loadGoogleMaps = (apiKey) => {
  if (!apiKey) return Promise.reject(new Error('Google Maps API key is not configured.'));
  if (window.google?.maps) return Promise.resolve(window.google);
  if (googleMapsPromise) return googleMapsPromise;

  googleMapsPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(GOOGLE_MAPS_SCRIPT_ID);
    if (existing) {
      existing.addEventListener('load', () => resolve(window.google));
      existing.addEventListener('error', reject);
      return;
    }

    const script = document.createElement('script');
    script.id = GOOGLE_MAPS_SCRIPT_ID;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    window.gm_authFailure = () => {
      googleMapsPromise = null;
      const error = new Error('Google Maps could not authenticate this API key. Check billing, referrer restrictions, and that Maps JavaScript API plus Places API are enabled.');
      window.dispatchEvent(new CustomEvent('magnolia-google-maps-auth-failure', { detail: error.message }));
      reject(error);
    };
    script.onload = () => resolve(window.google);
    script.onerror = () => reject(new Error('Unable to load Google Maps.'));
    document.head.appendChild(script);
  });

  return googleMapsPromise;
};

const toCoordinate = (value) => {
  if (!value) return null;
  const raw = value.coordinates || value;
  const latitude = raw.latitude ?? raw.lat;
  const longitude = raw.longitude ?? raw.lng;
  if (latitude === undefined || longitude === undefined) return null;
  const lat = Number(latitude);
  const lng = Number(longitude);
  if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  if (!isNigeriaCoordinate(lat, lng)) return null;
  return { latitude: lat, longitude: lng, lat, lng };
};

const GoogleMapLocationPicker = ({ value, addressHint = '', onChange, required = false }) => {
  const apiKey = sanitizeApiKey(process.env.REACT_APP_GOOGLE_MAPS_API_KEY);
  const mapRef = useRef(null);
  const inputRef = useRef(null);
  const markerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [loadError, setLoadError] = useState('');
  const [manualError, setManualError] = useState('');
  const initialCoordinates = toCoordinate(value);
  const [manualLatitude, setManualLatitude] = useState(initialCoordinates?.latitude || '');
  const [manualLongitude, setManualLongitude] = useState(initialCoordinates?.longitude || '');
  const coordinates = toCoordinate(value);
  const coordinateLat = coordinates?.latitude;
  const coordinateLng = coordinates?.longitude;

  useEffect(() => {
    const next = toCoordinate(value);
    setManualLatitude(next?.latitude || '');
    setManualLongitude(next?.longitude || '');
  }, [value]);

  useEffect(() => {
    const handleAuthFailure = (event) => {
      setLoadError(event.detail || 'Google Maps could not authenticate this API key.');
    };
    window.addEventListener('magnolia-google-maps-auth-failure', handleAuthFailure);
    return () => window.removeEventListener('magnolia-google-maps-auth-failure', handleAuthFailure);
  }, []);

  useEffect(() => {
    if (!apiKey) {
      setLoadError('Google Maps is not configured yet. Use the coordinate fields for local testing.');
      return;
    }

    let cancelled = false;
    loadGoogleMaps(apiKey)
      .then((google) => {
        if (cancelled || !mapRef.current) return;
        const hasCoordinates = coordinateLat !== undefined && coordinateLng !== undefined;
        const center = hasCoordinates ? { lat: coordinateLat, lng: coordinateLng } : { lat: 6.5244, lng: 3.3792 };
        const map = new google.maps.Map(mapRef.current, {
          center,
          zoom: hasCoordinates ? 16 : 11,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        });
        const marker = new google.maps.Marker({
          map,
          position: center,
          draggable: true,
          visible: hasCoordinates,
        });
        const applyPosition = (position, locationSource, formattedAddress = inputRef.current?.value || addressHint) => {
          if (!position) return;
          const lat = position.lat();
          const lng = position.lng();
          if (!isNigeriaCoordinate(lat, lng)) {
            setManualError('Choose a home location inside Nigeria.');
            return;
          }
          setManualError('');
          marker.setPosition({ lat, lng });
          marker.setVisible(true);
          onChange({
            coordinates: {
              latitude: lat,
              longitude: lng,
              lat,
              lng,
            },
            formattedAddress,
            placeId: value?.placeId || value?.place_id || '',
            locationSource,
            locationConfirmedAt: new Date().toISOString(),
          });
        };
        marker.addListener('dragend', () => {
          applyPosition(marker.getPosition(), 'google_maps_drag');
        });
        map.addListener('click', (event) => {
          applyPosition(event.latLng, 'google_maps_click');
        });
        mapInstanceRef.current = map;
        markerRef.current = marker;

        if (inputRef.current && google.maps.places?.Autocomplete) {
          const autocomplete = new google.maps.places.Autocomplete(inputRef.current, {
            componentRestrictions: { country: 'ng' },
            fields: ['formatted_address', 'geometry', 'place_id'],
          });
          autocomplete.addListener('place_changed', () => {
            const place = autocomplete.getPlace();
            const location = place.geometry?.location;
            if (!location) return;
            const next = {
              lat: location.lat(),
              lng: location.lng(),
            };
            if (!isNigeriaCoordinate(next.lat, next.lng)) {
              setManualError('Choose a Nigerian address for the home pin.');
              return;
            }
            setManualError('');
            marker.setPosition(next);
            marker.setVisible(true);
            map.setCenter(next);
            map.setZoom(17);
            onChange({
              coordinates: {
                latitude: next.lat,
                longitude: next.lng,
                lat: next.lat,
                lng: next.lng,
              },
              formattedAddress: place.formatted_address || inputRef.current.value,
              placeId: place.place_id || '',
              locationSource: 'google_maps_picker',
              locationConfirmedAt: new Date().toISOString(),
            });
          });
        } else {
          setLoadError('Google Places autocomplete is unavailable. Enable Places API for search, or click the map and use the coordinate fields.');
        }
      })
      .catch((error) => setLoadError(error.message || 'Unable to load Google Maps.'));

    return () => {
      cancelled = true;
    };
  }, [apiKey, addressHint, coordinateLat, coordinateLng, onChange, value?.placeId, value?.place_id]);

  useEffect(() => {
    if (coordinateLat === undefined || coordinateLng === undefined || !markerRef.current || !mapInstanceRef.current) return;
    markerRef.current.setPosition({ lat: coordinateLat, lng: coordinateLng });
    markerRef.current.setVisible(true);
    mapInstanceRef.current.setCenter({ lat: coordinateLat, lng: coordinateLng });
  }, [coordinateLat, coordinateLng]);

  useEffect(() => {
    if (manualLatitude === '' || manualLongitude === '') return;
    const next = toCoordinate({ latitude: manualLatitude, longitude: manualLongitude });
    if (!next) return;
    if (
      coordinateLat !== undefined &&
      coordinateLng !== undefined &&
      Math.abs(Number(coordinateLat) - next.latitude) < 0.000001 &&
      Math.abs(Number(coordinateLng) - next.longitude) < 0.000001
    ) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setManualError('');
      onChange({
        coordinates: next,
        formattedAddress: addressHint,
        placeId: '',
        locationSource: apiKey ? 'manual_coordinate_entry' : 'manual_coordinate_entry_dev',
        locationConfirmedAt: new Date().toISOString(),
      });
    }, 350);

    return () => window.clearTimeout(timeoutId);
  }, [manualLatitude, manualLongitude, coordinateLat, coordinateLng, addressHint, apiKey, onChange]);

  const applyManualCoordinates = () => {
    const coordinates = toCoordinate({ latitude: manualLatitude, longitude: manualLongitude });
    if (!coordinates) {
      setManualError('Enter a valid latitude and longitude inside Nigeria.');
      return;
    }
    setManualError('');
    onChange({
      coordinates,
      formattedAddress: addressHint,
      placeId: '',
      locationSource: apiKey ? 'manual_coordinate_entry' : 'manual_coordinate_entry_dev',
      locationConfirmedAt: new Date().toISOString(),
    });
  };

  const applyMapCenter = () => {
    const center = mapInstanceRef.current?.getCenter?.();
    if (!center) {
      setManualError('Move the map or enter coordinates before setting the pin.');
      return;
    }
    const coordinates = toCoordinate({ latitude: center.lat(), longitude: center.lng() });
    if (!coordinates) {
      setManualError('Center the map on a Nigerian home location before setting the pin.');
      return;
    }
    setManualError('');
    markerRef.current?.setPosition({ lat: coordinates.lat, lng: coordinates.lng });
    markerRef.current?.setVisible(true);
    onChange({
      coordinates,
      formattedAddress: inputRef.current?.value || addressHint,
      placeId: '',
      locationSource: 'google_maps_center',
      locationConfirmedAt: new Date().toISOString(),
    });
  };

  return (
    <div className="space-y-3 rounded-lg border border-[#E8E6E3] bg-[#FAFAF7] p-4 sm:col-span-2">
      <div className="flex flex-col gap-1">
        <Label>Set exact home location{required ? ' *' : ''}</Label>
        <p className="text-xs text-[#5A5A5A]">Search the address, click the map, or drag the pin until it sits on the dependant's home.</p>
      </div>

      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#5A5A5A]" />
        <Input ref={inputRef} defaultValue={value?.formattedAddress || value?.formatted_address || addressHint} className="pl-9" placeholder="Search address or landmark" />
      </div>

      <div ref={mapRef} className="h-72 overflow-hidden rounded-lg border border-[#E8E6E3] bg-[#F7F4EE]" />

      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" onClick={applyMapCenter}>
          Set pin at map center
        </Button>
      </div>

      {loadError && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          {loadError}
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
        <Input
          type="number"
          step="any"
          placeholder="Latitude"
          value={manualLatitude}
          onChange={(event) => setManualLatitude(event.target.value)}
        />
        <Input
          type="number"
          step="any"
          placeholder="Longitude"
          value={manualLongitude}
          onChange={(event) => setManualLongitude(event.target.value)}
        />
        <Button type="button" variant="outline" onClick={applyManualCoordinates}>
          Set Pin
        </Button>
      </div>
      {manualError && <p className="text-xs text-red-700">{manualError}</p>}

      {coordinates ? (
        <p className="flex items-center gap-2 text-sm font-medium text-green-700">
          <MapPin className="h-4 w-4" />
          Home pin confirmed: {coordinates.latitude.toFixed(6)}, {coordinates.longitude.toFixed(6)}
        </p>
      ) : (
        <p className="flex items-center gap-2 text-sm text-red-700">
          <MapPin className="h-4 w-4" />
          Home pin is required for accurate visit verification.
        </p>
      )}
    </div>
  );
};

export default GoogleMapLocationPicker;
